import { useState } from 'react';
import { api, type CalcRunWithSteps, type EmiDetails, type EmiRestriction } from '../lib/api';
import { longDate, money, pct } from '../lib/format';
import { Badge, Card, EmptyState, SectionHeading } from './ui';
import { Icon } from './icons';

/**
 * The VAL231 pack: the two values HMRC's form asks for, and the scheme facts
 * they are computed from.
 *
 * An EMI valuation is not one number. The **unrestricted market value**
 * ignores the restrictions attaching to the shares and is what the statutory
 * limits are measured against; the **actual market value** takes them into
 * account and is what the exercise price is set against. Grant below AMV and
 * the employees carry an income tax charge on exercise they will not find out
 * about until they exercise.
 *
 * So this panel is arranged around the gap between the two. The restrictions
 * are entered as the clause that creates them and the discount attributed to
 * it — never as one unexplained percentage, because itemising them is exactly
 * what a Shares & Assets Valuation officer asks for and the person who can
 * name the clause is the one filling this in.
 *
 * Nothing here is typed twice. The per-share value comes from the holding
 * value already calculated, and the statutory limits from the corpus as it
 * stood at the valuation date — the individual limit was £120,000 until June
 * 2012, and a preparer typing today's figure onto a 2011 valuation would be
 * wrong in a way nobody notices.
 */
export function EmiPack({
  engagementId,
  details,
  run,
  canWrite,
  onChange,
}: {
  engagementId: string;
  details: EmiDetails | null | undefined;
  /** The latest successful emi_share_values run, if there is one. */
  run: CalcRunWithSteps | null;
  canWrite: boolean;
  onChange: () => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const out = (run?.outputs ?? {}) as Record<string, any>;
  const stale = Boolean(details && run && new Date(details.updatedAt) > new Date(run.createdAt));

  const compute = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.runEmiShareValues(engagementId);
      await onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not compute the VAL231 figures');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <SectionHeading
        title="EMI — VAL231 submission figures"
        subtitle="Two values per share: the limits are measured against one, the exercise price is set against the other"
        right={
          canWrite ? (
            <button type="button" className="btn-secondary" onClick={() => setEditing((v) => !v)}>
              <Icon name="plus" className="h-4 w-4" strokeWidth={2} />
              {details ? 'Edit the scheme facts' : 'Record the scheme facts'}
            </button>
          ) : null
        }
      />

      {editing ? (
        <EmiDetailsForm
          engagementId={engagementId}
          details={details}
          onDone={async (saved) => {
            setEditing(false);
            if (saved) await onChange();
          }}
        />
      ) : null}

      {!details ? (
        <EmptyState
          title="No scheme facts recorded"
          detail="The pack needs the share class, what is under option, and the restrictions in the articles — the restrictions are the whole of the gap between the two values."
        />
      ) : !run ? (
        <div className="px-6 py-6">
          <p className="text-sm leading-relaxed text-slate-600">
            The scheme facts are recorded. The figures are computed from the per-share value the
            holding calculation already produced and the statutory limits in force at the valuation
            date — so there is nothing further to type.
          </p>
          {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}
          {canWrite ? (
            <button type="button" className="btn-primary mt-4" disabled={busy} onClick={compute}>
              {busy ? 'Computing…' : 'Compute the VAL231 figures'}
            </button>
          ) : null}
        </div>
      ) : (
        <div className="space-y-5 px-6 py-5">
          {stale ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              The scheme facts have changed since these figures were computed. Recompute before the
              pack goes anywhere.
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <ValueTile
              label="Unrestricted market value (UMV)"
              value={out.unrestricted_value_per_share}
              note="Ignores the restrictions. The statutory limits are measured against this."
              tone="slate"
            />
            <ValueTile
              label="Actual market value (AMV)"
              value={out.actual_value_per_share}
              note="Takes the restrictions into account. The exercise price is set against this."
              tone="teal"
            />
          </div>

          <div className="flex flex-wrap gap-1.5">
            <Badge tone="slate">
              restriction discount {pct(out.restriction_discount_pct ?? '0', 2)}
            </Badge>
            <Badge tone="slate">{out.shares_under_option} shares under option</Badge>
            {out.exercise_price_per_share ? (
              <Badge tone={out.income_tax_charge_on_exercise ? 'amber' : 'emerald'}>
                exercise at {money(out.exercise_price_per_share)}
                {out.income_tax_charge_on_exercise
                  ? ` · ${money(out.discount_to_amv_per_share)} below AMV`
                  : ' · at or above AMV'}
              </Badge>
            ) : (
              <Badge tone="amber">no exercise price set</Badge>
            )}
          </div>

          {out.income_tax_charge_on_exercise ? (
            <p className="text-sm leading-relaxed text-amber-800">
              The option is priced {money(out.discount_to_amv_per_share)} per share below actual
              market value. Under ITEPA 2003 s.531 the employees bear income tax on{' '}
              {money(out.discount_to_amv_total)} across the grant when they exercise.
            </p>
          ) : null}

          {(out.restrictions ?? []).length > 0 ? (
            <div>
              <p className="label">Restrictions relied on</p>
              <ul className="mt-1.5 space-y-1">
                {(out.restrictions as { label: string; discount: string }[]).map((r, i) => (
                  <li key={i} className="text-sm text-slate-600">
                    {r.label} — {pct(r.discount, 2)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <Limits out={out} />

          {/* The engine's warnings are not re-read from the run — they are
              not persisted on it — but nothing is lost: every one of them is
              a statement about a limit or the exercise price, and both are
              rendered above from the outputs themselves. */}
          <div className="flex items-center gap-3">
            {canWrite ? (
              <button type="button" className="btn-secondary" disabled={busy} onClick={compute}>
                {busy ? 'Recomputing…' : 'Recompute'}
              </button>
            ) : null}
            <span className="text-xs text-slate-400">
              Computed {longDate(run.createdAt.slice(0, 10))} from the holding value and the limits
              in force at the valuation date.
            </span>
          </div>
          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        </div>
      )}
    </Card>
  );
}

function ValueTile({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string | undefined;
  note: string;
  tone: 'slate' | 'teal';
}) {
  return (
    <div
      className={`rounded-xl border px-4 py-3.5 ${
        tone === 'teal' ? 'border-teal-200 bg-teal-50/50' : 'border-slate-200 bg-slate-50/60'
      }`}
    >
      <p className="label">{label}</p>
      <p className="tnum mt-1 text-2xl font-semibold text-slate-900">
        {value ? money(value) : '—'}
      </p>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">{note}</p>
    </div>
  );
}

/**
 * A limit that was not resolved is shown as untested rather than omitted.
 * Silence would read as a pass, and the whole point of holding the limits as
 * effective-dated data is that the answer depends on the valuation date.
 */
function Limits({ out }: { out: Record<string, any> }) {
  const grants = (out.grants ?? []) as {
    employee_ref: string;
    shares: string;
    unrestricted_value: string;
    within_individual_limit: boolean | null;
    headroom: string | null;
  }[];

  return (
    <div className="space-y-3">
      <div>
        <p className="label">Individual limit — ITEPA 2003 Sch 5 para 5</p>
        {!out.individual_umv_limit ? (
          <p className="mt-1 text-sm text-amber-800">
            Not tested: no limit is in force in the corpus at this valuation date.
          </p>
        ) : grants.length === 0 ? (
          <p className="mt-1 text-sm text-amber-800">
            Not tested: {money(out.individual_umv_limit)} per employee, but no per-employee grants
            were recorded. A pool total says nothing about what one employee holds.
          </p>
        ) : (
          <ul className="mt-1.5 divide-y divide-slate-100">
            {grants.map((g, i) => (
              <li key={i} className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
                <span className="text-slate-700">
                  {g.employee_ref} · {g.shares} shares
                </span>
                <span className="flex items-center gap-2">
                  <span className="tnum text-slate-600">{money(g.unrestricted_value)}</span>
                  <Badge tone={g.within_individual_limit === false ? 'rose' : 'emerald'}>
                    {g.within_individual_limit === false
                      ? `${money(g.headroom ?? '0')} over`
                      : `${money(g.headroom ?? '0')} headroom`}
                  </Badge>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <p className="label">Company limit — ITEPA 2003 Sch 5 para 7</p>
        {!out.company_umv_limit ? (
          <p className="mt-1 text-sm text-amber-800">
            Not tested: no limit is in force in the corpus at this valuation date.
          </p>
        ) : (
          <p className="mt-1 text-sm text-slate-600">
            {money(out.company_umv_committed_total)} of unexercised options against a limit of{' '}
            {money(out.company_umv_limit)} —{' '}
            {out.within_company_limit ? (
              <span className="text-emerald-700">
                {money(out.company_limit_headroom)} of headroom.
              </span>
            ) : (
              <span className="text-rose-700">
                {money(out.company_limit_headroom)}, which exceeds it.
              </span>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------

const INPUT =
  'mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';

function EmiDetailsForm({
  engagementId,
  details,
  onDone,
}: {
  engagementId: string;
  details: EmiDetails | null | undefined;
  onDone: (saved: boolean) => void | Promise<void>;
}) {
  const [shareClass, setShareClass] = useState(details?.shareClass ?? 'ordinary');
  const [grantDate, setGrantDate] = useState(details?.grantDate ?? '');
  const [sharesUnderOption, setSharesUnderOption] = useState(
    details?.sharesUnderOption ? String(Number(details.sharesUnderOption)) : '',
  );
  const [exercisePrice, setExercisePrice] = useState(
    details?.exercisePricePerShare ? String(Number(details.exercisePricePerShare)) : '',
  );
  const [alreadyCommitted, setAlreadyCommitted] = useState(
    details?.companyUmvAlreadyCommitted ? String(Number(details.companyUmvAlreadyCommitted)) : '',
  );
  const [articlesReference, setArticlesReference] = useState(details?.articlesReference ?? '');
  const [restrictions, setRestrictions] = useState<EmiRestriction[]>(
    details?.restrictions?.length ? details.restrictions : [{ label: '', discount: '' }],
  );
  const [grants, setGrants] = useState<{ employeeRef: string; shares: string }[]>(
    details?.grants?.length
      ? details.grants.map((g) => ({ employeeRef: g.employee_ref, shares: String(Number(g.shares)) }))
      : [{ employeeRef: '', shares: '' }],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const usableRestrictions = restrictions.filter(
    (r) => r.label.trim().length > 2 && /^0(\.\d+)?$/.test(r.discount.trim()),
  );
  const usableGrants = grants.filter(
    (g) => g.employeeRef.trim() && /^\d+(\.\d+)?$/.test(g.shares.trim()),
  );
  const valid = Boolean(sharesUnderOption.trim() || usableGrants.length > 0);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.recordEmiDetails(engagementId, {
        shareClass: shareClass.trim() || 'ordinary',
        grantDate: grantDate || null,
        sharesUnderOption: sharesUnderOption.trim() || null,
        exercisePricePerShare: exercisePrice.trim() || null,
        restrictions: usableRestrictions.map((r) => ({
          label: r.label.trim(),
          discount: r.discount.trim(),
          justification: r.justification?.trim() || undefined,
        })),
        grants: usableGrants.map((g) => ({
          employeeRef: g.employeeRef.trim(),
          shares: g.shares.trim(),
        })),
        companyUmvAlreadyCommitted: alreadyCommitted.trim() || undefined,
        articlesReference: articlesReference.trim() || undefined,
      });
      await onDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the scheme facts');
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 border-b border-slate-100 bg-slate-50/60 px-6 py-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="label">Share class under option</span>
          <input value={shareClass} onChange={(e) => setShareClass(e.target.value)} className={INPUT} />
        </label>
        <label className="block">
          <span className="label">Date of grant</span>
          <input
            type="date"
            value={grantDate}
            onChange={(e) => setGrantDate(e.target.value)}
            className={`tnum ${INPUT}`}
          />
        </label>
        <label className="block">
          <span className="label">Shares under option, in total</span>
          <input
            inputMode="decimal"
            value={sharesUnderOption}
            onChange={(e) => setSharesUnderOption(e.target.value)}
            placeholder="145000"
            className={`tnum ${INPUT}`}
          />
          <span className="mt-1 block text-xs text-slate-400">
            The grants below override this where they are known.
          </span>
        </label>
        <label className="block">
          <span className="label">Exercise price per share</span>
          <input
            inputMode="decimal"
            value={exercisePrice}
            onChange={(e) => setExercisePrice(e.target.value)}
            placeholder="Leave blank if not yet set"
            className={`tnum ${INPUT}`}
          />
          <span className="mt-1 block text-xs text-slate-400">
            Below the actual market value and the employees bear income tax on the difference at
            exercise.
          </span>
        </label>
      </div>

      <div>
        <p className="label">Restrictions attaching to the shares</p>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">
          Name the clause and the discount you attribute to it. This is the whole of the gap between
          the two values on the form, and “a 10% discount for restrictions” is the first thing an
          officer asks to see broken down.
        </p>
        <div className="mt-2 space-y-2">
          {restrictions.map((r, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-[1fr_7rem]">
              <input
                value={r.label}
                onChange={(e) =>
                  setRestrictions((rs) => rs.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))
                }
                placeholder="e.g. Compulsory transfer at cost on ceasing employment (art. 12)"
                className={INPUT}
              />
              <input
                inputMode="decimal"
                value={r.discount}
                onChange={(e) =>
                  setRestrictions((rs) =>
                    rs.map((x, j) => (j === i ? { ...x, discount: e.target.value } : x)),
                  )
                }
                placeholder="0.10"
                className={`tnum ${INPUT}`}
              />
            </div>
          ))}
        </div>
        <button
          type="button"
          className="mt-2 text-xs font-medium text-teal-700 hover:text-teal-900"
          onClick={() => setRestrictions((rs) => [...rs, { label: '', discount: '' }])}
        >
          Add another restriction
        </button>
      </div>

      <div>
        <p className="label">Proposed grants, by employee</p>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">
          The individual limit is a limit on what one employee holds, so a pool total cannot be
          tested against it.
        </p>
        <div className="mt-2 space-y-2">
          {grants.map((g, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-[1fr_9rem]">
              <input
                value={g.employeeRef}
                onChange={(e) =>
                  setGrants((gs) => gs.map((x, j) => (j === i ? { ...x, employeeRef: e.target.value } : x)))
                }
                placeholder="e.g. A. Renwick, Head of Engineering"
                className={INPUT}
              />
              <input
                inputMode="decimal"
                value={g.shares}
                onChange={(e) =>
                  setGrants((gs) => gs.map((x, j) => (j === i ? { ...x, shares: e.target.value } : x)))
                }
                placeholder="30000"
                className={`tnum ${INPUT}`}
              />
            </div>
          ))}
        </div>
        <button
          type="button"
          className="mt-2 text-xs font-medium text-teal-700 hover:text-teal-900"
          onClick={() => setGrants((gs) => [...gs, { employeeRef: '', shares: '' }])}
        >
          Add another employee
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="label">EMI options already outstanding (UMV)</span>
          <input
            inputMode="decimal"
            value={alreadyCommitted}
            onChange={(e) => setAlreadyCommitted(e.target.value)}
            placeholder="0"
            className={`tnum ${INPUT}`}
          />
          <span className="mt-1 block text-xs text-slate-400">
            Counted with this grant against the company limit.
          </span>
        </label>
        <label className="block">
          <span className="label">Where the restrictions are written down</span>
          <input
            value={articlesReference}
            onChange={(e) => setArticlesReference(e.target.value)}
            placeholder="e.g. Articles of association adopted 4 March 2024, arts. 11–14"
            className={INPUT}
          />
        </label>
      </div>

      {error ? <p className="text-sm text-rose-600">{error}</p> : null}

      <div className="flex items-center justify-end gap-2">
        <button type="button" className="btn-secondary" disabled={busy} onClick={() => onDone(false)}>
          Cancel
        </button>
        <button type="button" className="btn-primary" disabled={!valid || busy} onClick={submit}>
          {busy ? 'Recording…' : 'Record the scheme facts'}
        </button>
      </div>
    </div>
  );
}
